import { ImageField } from "../ImageField";
import { useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router";
import {
  isAuthorStory,
  type AuthorStory,
  type ChapterDetail,
  type Item,
  type SceneDetail,
  type UpdateSceneBody,
} from "@storyai/shared";
import { ConditionInput, conditionIsValid } from "../ConditionInput";
import {
  Badge,
  EmptyState,
  ErrorPanel,
  Field,
  InlineConfirm,
  Loading,
  MutationError,
  PageHeader,
  SavedFlash,
} from "../ui";
import { ApiError } from "../../lib/api";
import {
  useChapter,
  useConnectionMutations,
  useDeleteScene,
  useItemMutations,
  useScene,
  useStory,
  useUpdateScene,
} from "../../lib/queries";

interface SceneForm {
  name: string;
  key: string;
  gmNotes: string;
}

function toForm(s: SceneDetail): SceneForm {
  return { name: s.name, key: s.key, gmNotes: s.gmNotes };
}

function toBody(f: SceneForm): UpdateSceneBody {
  return { name: f.name.trim(), key: f.key.trim(), gmNotes: f.gmNotes };
}

export interface SceneEditorProps {
  storyId: string;
  chapterId: string;
  sceneId: string;
  compact?: boolean;
  onDeleted?: () => void;
  onSelectScene?: (sceneId: string) => void;
  onSelectConnection?: (connectionId: string) => void;
}

export function SceneEditor(props: SceneEditorProps) {
  const sq = useStory(props.storyId);
  const cq = useChapter(props.storyId, props.chapterId);
  const scq = useScene(props.storyId, props.chapterId, props.sceneId);

  if (sq.isPending || cq.isPending || scq.isPending) return <Loading />;
  if (sq.isError) return <ErrorPanel error={sq.error} />;
  if (cq.isError) return <ErrorPanel error={cq.error} />;
  if (scq.isError) return <ErrorPanel error={scq.error} />;
  if (!sq.data.isAuthor || !isAuthorStory(sq.data.story)) {
    return <ErrorPanel error={new ApiError(403, "Forbidden")} />;
  }
  return <SceneForm story={sq.data.story} chapter={cq.data.chapter} scene={scq.data.scene} {...props} />;
}

function SceneForm({
  story,
  chapter,
  scene,
  compact,
  onDeleted,
  onSelectScene,
  onSelectConnection,
}: SceneEditorProps & { story: AuthorStory; chapter: ChapterDetail; scene: SceneDetail }) {
  const update = useUpdateScene(story.id, chapter.id, scene.id);
  const del = useDeleteScene(story.id, chapter.id);
  const [form, setForm] = useState<SceneForm>(() => toForm(scene));
  const [baseline, setBaseline] = useState(() => JSON.stringify(toForm(scene)));
  const [savedToken, setSavedToken] = useState(0);
  const dirty = JSON.stringify(form) !== baseline;

  useEffect(() => {
    if (!dirty) {
      const f = toForm(scene);
      setForm(f);
      setBaseline(JSON.stringify(f));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scene.name, scene.key, scene.gmNotes]);

  const set = <K extends keyof SceneForm>(k: K, v: SceneForm[K]) => setForm((f) => ({ ...f, [k]: v }));

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const { scene: saved } = await update.mutateAsync(toBody(form));
    const f = toForm(saved);
    setForm(f);
    setBaseline(JSON.stringify(f));
    setSavedToken((t) => t + 1);
  }

  return (
    <div className={compact ? "editor-compact" : "page page-narrow"}>
      {!compact && (
        <PageHeader
          crumbs={[
            { to: "/my-stories", label: "My Stories" },
            { to: `/stories/${story.id}`, label: story.title },
            { to: `/stories/${story.id}/chapters/${chapter.id}`, label: chapter.title },
            { label: scene.name },
          ]}
          title={scene.name}
          subtitle={
            <>
              Scene <code>{scene.key}</code>
              {chapter.startSceneId === scene.id && (
                <>
                  {" "}
                  · <Badge tone="accent">Start</Badge>
                </>
              )}
            </>
          }
          actions={
            <Link to={`/stories/${story.id}/map?scene=${scene.id}`} className="btn btn-sm">
              Open in map
            </Link>
          }
        />
      )}

      <form className="form" onSubmit={onSubmit}>
        <section className="section">
          <div className="form-grid">
            <Field label="Name">
              <input
                type="text"
                value={form.name}
                required
                maxLength={120}
                onChange={(e) => set("name", e.target.value)}
              />
            </Field>
            <Field label="Key" hint="Unique within the chapter. Lowercase, digits, underscores.">
              <input
                type="text"
                className="mono"
                value={form.key}
                required
                pattern="[a-z0-9_]{1,40}"
                onChange={(e) => set("key", e.target.value)}
              />
            </Field>
          </div>
          <Field
            label="GM notes (hidden from players)"
            hint="What's physically here, what's hidden and where, who's present, what they know and want. This is what the AI reads to answer “I look in the corner.”"
          >
            <textarea
              rows={compact ? 7 : 10}
              value={form.gmNotes}
              onChange={(e) => set("gmNotes", e.target.value)}
            />
          </Field>
        </section>
        <MutationError error={update.error} />
        <div className="form-actions sticky-actions">
          <button type="submit" className="btn btn-primary" disabled={!dirty || update.isPending}>
            {update.isPending ? "Saving…" : "Save"}
          </button>
          {dirty ? <span className="muted small">Unsaved changes</span> : <SavedFlash token={savedToken} />}
        </div>
      </form>

      <ItemsSection story={story} chapter={chapter} scene={scene} compact={!!compact} />
      <ConnectionsSection
        story={story}
        chapter={chapter}
        scene={scene}
        onSelectScene={onSelectScene}
        onSelectConnection={onSelectConnection}
      />

      <section className="section danger-zone">
        <h2>Danger zone</h2>
        {chapter.scenes.length <= 1 ? (
          <p className="small muted">
            This is the chapter's only scene, so it can't be deleted. A chapter needs at least one
            scene. Add another scene first, or delete the chapter.
          </p>
        ) : (
          <>
            <p className="small">Deleting this scene removes its items and every connection to or from it.</p>
            <InlineConfirm
              label="Delete scene"
              question="Delete this scene?"
              confirmLabel="Delete"
              busy={del.isPending}
              onConfirm={async () => {
                await del.mutateAsync(scene.id);
                onDeleted?.();
              }}
            />
          </>
        )}
        <MutationError error={del.error} />
      </section>
    </div>
  );
}

// ---- Items ------------------------------------------------------------------

interface ItemForm {
  name: string;
  key: string;
  description: string;
  howFound: string;
  isPlot: boolean;
  /** Inventory icon URL; only editable on existing items. */
  imageUrl: string | null;
}

const EMPTY_ITEM: ItemForm = { name: "", key: "", description: "", howFound: "", isPlot: false, imageUrl: null };

/** Small transparent-friendly thumbnail for an item icon, or a placeholder square. */
function ItemThumb({ url, size = 40 }: { url: string | null; size?: number }) {
  return (
    <span className={`item-thumb${url ? "" : " empty"}`} style={{ width: size, height: size }} aria-hidden="true">
      {url && <img src={url} alt="" />}
    </span>
  );
}

function ItemsSection({
  story,
  chapter,
  scene,
  compact,
}: {
  story: AuthorStory;
  chapter: ChapterDetail;
  scene: SceneDetail;
  compact: boolean;
}) {
  const { create, update, remove } = useItemMutations(story.id, chapter.id, scene.id);
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const busy = update.isPending || remove.isPending;

  const addForm = adding && (
    <ItemFields
      storyId={story.id}
      showIcon={false}
      initial={EMPTY_ITEM}
      busy={create.isPending}
      onCancel={() => setAdding(false)}
      onSave={async (f) => {
        await create.mutateAsync({
          name: f.name.trim(),
          key: f.key.trim() || undefined,
          description: f.description,
          howFound: f.howFound,
          isPlot: f.isPlot,
        });
        setAdding(false);
      }}
    />
  );

  const saveItem = (it: Item) => async (f: ItemForm) => {
    await update.mutateAsync({
      itemId: it.id,
      body: {
        name: f.name.trim(),
        key: f.key.trim(),
        description: f.description,
        howFound: f.howFound,
        isPlot: f.isPlot,
        imageUrl: f.imageUrl,
      },
    });
    setEditingId(null);
  };

  return (
    <section className="section">
      <div className="section-head">
        <h2>Items</h2>
        {!adding && (
          <button type="button" className="btn btn-sm btn-primary" onClick={() => setAdding(true)}>
            Add item
          </button>
        )}
      </div>
      <p className="muted small">
        Things the player can discover here. Plot items gate conditions (<code>has:key</code>); flavor
        items are just texture. Item keys are unique across the whole story.
      </p>
      {scene.items.length === 0 && !adding ? (
        <EmptyState title="No items in this scene" />
      ) : compact ? (
        <ul className="stack-list">
          {scene.items.map((it) =>
            editingId === it.id ? (
              <li key={it.id} className="stack-item">
                <ItemFields
                  storyId={story.id}
                  initial={{
                    name: it.name,
                    key: it.key,
                    description: it.description,
                    howFound: it.howFound,
                    isPlot: it.isPlot,
                    imageUrl: it.imageUrl,
                  }}
                  busy={busy}
                  onCancel={() => setEditingId(null)}
                  onSave={saveItem(it)}
                />
              </li>
            ) : (
              <li key={it.id} className="stack-item">
                <ItemThumb url={it.imageUrl} />
                <div className="stack-item-main">
                  <div className="stack-item-title">
                    {it.name}{" "}
                    {it.isPlot ? <Badge tone="accent">Plot</Badge> : <Badge tone="muted">Flavor</Badge>}
                  </div>
                  <div className="muted small">
                    <code>{it.key}</code>
                    {it.description && <> · {it.description}</>}
                  </div>
                  {it.howFound && <div className="muted small">Found: {it.howFound}</div>}
                </div>
                <div className="stack-item-actions">
                  <button type="button" className="btn btn-sm" onClick={() => setEditingId(it.id)} disabled={busy}>
                    Edit
                  </button>
                  <InlineConfirm label="Delete" onConfirm={() => remove.mutate(it.id)} busy={busy} />
                </div>
              </li>
            ),
          )}
          {adding && <li className="stack-item">{addForm}</li>}
        </ul>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Key</th>
                <th>Name</th>
                <th>Type</th>
                <th>How it's found</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {scene.items.map((it) =>
                editingId === it.id ? (
                  <tr key={it.id} className="edit-row">
                    <td colSpan={5}>
                      <ItemFields
                        storyId={story.id}
                        initial={{
                          name: it.name,
                          key: it.key,
                          description: it.description,
                          howFound: it.howFound,
                          isPlot: it.isPlot,
                          imageUrl: it.imageUrl,
                        }}
                        busy={busy}
                        onCancel={() => setEditingId(null)}
                        onSave={saveItem(it)}
                      />
                    </td>
                  </tr>
                ) : (
                  <tr key={it.id}>
                    <td>
                      <span className="item-key-cell">
                        <ItemThumb url={it.imageUrl} />
                        <code>{it.key}</code>
                      </span>
                    </td>
                    <td>
                      <div>{it.name}</div>
                      {it.description && <div className="muted small">{it.description}</div>}
                    </td>
                    <td>{it.isPlot ? <Badge tone="accent">Plot</Badge> : <Badge tone="muted">Flavor</Badge>}</td>
                    <td className="muted small">{it.howFound}</td>
                    <td className="row-actions">
                      <button
                        type="button"
                        className="btn btn-sm"
                        onClick={() => setEditingId(it.id)}
                        disabled={busy}
                      >
                        Edit
                      </button>
                      <InlineConfirm label="Delete" onConfirm={() => remove.mutate(it.id)} busy={busy} />
                    </td>
                  </tr>
                ),
              )}
              {adding && (
                <tr className="edit-row">
                  <td colSpan={5}>{addForm}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      <MutationError error={create.error ?? update.error ?? remove.error} />
    </section>
  );
}

function ItemFields({
  initial,
  busy,
  onCancel,
  onSave,
  storyId,
  showIcon = true,
}: {
  initial: ItemForm;
  busy: boolean;
  onCancel: () => void;
  onSave: (f: ItemForm) => Promise<void>;
  storyId: string;
  /** Icons are set on existing items (creation has no imageUrl field). */
  showIcon?: boolean;
}) {
  const [f, setF] = useState<ItemForm>(initial);
  const keyOk = f.key === "" || /^[a-z0-9_]{1,40}$/.test(f.key);
  const valid = f.name.trim() !== "" && keyOk;
  return (
    <div className="form">
      {showIcon && (
        <ImageField
          square
          label="Icon"
          storyId={storyId}
          value={f.imageUrl}
          onChange={(url) => setF({ ...f, imageUrl: url })}
          hint="Transparent PNG/WebP works best. The AI editor can generate one."
        />
      )}
      <div className="form-grid">
        <Field label="Name">
          <input type="text" value={f.name} required onChange={(e) => setF({ ...f, name: e.target.value })} />
        </Field>
        <Field label="Key" hint="Leave blank to derive from the name.">
          <input
            type="text"
            className={`mono ${keyOk ? "" : "input-invalid"}`}
            value={f.key}
            placeholder="auto from name"
            onChange={(e) => setF({ ...f, key: e.target.value })}
          />
        </Field>
      </div>
      <Field label="Description">
        <input type="text" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} />
      </Field>
      <Field label="How it's found" hint="Where it is and what the player has to do to find it.">
        <input type="text" value={f.howFound} onChange={(e) => setF({ ...f, howFound: e.target.value })} />
      </Field>
      <Field inline label="Plot item" hint="Needed by a condition somewhere.">
        <input type="checkbox" checked={f.isPlot} onChange={(e) => setF({ ...f, isPlot: e.target.checked })} />
      </Field>
      <div className="form-actions">
        <button
          type="button"
          className="btn btn-sm btn-primary"
          disabled={!valid || busy}
          onClick={() => void onSave(f)}
        >
          Save
        </button>
        <button type="button" className="btn btn-sm" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
    </div>
  );
}

// ---- Connections ------------------------------------------------------------

function ConnectionsSection({
  story,
  chapter,
  scene,
  onSelectScene,
  onSelectConnection,
}: {
  story: AuthorStory;
  chapter: ChapterDetail;
  scene: SceneDetail;
  onSelectScene?: (sceneId: string) => void;
  onSelectConnection?: (connectionId: string) => void;
}) {
  const { create, remove } = useConnectionMutations(story.id, chapter.id, scene.id);
  const [toSceneId, setToSceneId] = useState("");
  const [condition, setCondition] = useState("");
  const targets = scene.siblings.filter((s) => !scene.connections.some((c) => c.toSceneId === s.id));
  const valid = toSceneId !== "" && conditionIsValid(condition);

  async function onAdd(e: FormEvent) {
    e.preventDefault();
    await create.mutateAsync({ toSceneId, condition: condition.trim() || null });
    setToSceneId("");
    setCondition("");
  }

  return (
    <section className="section">
      <h2>Connections</h2>
      <p className="muted small">
        Which other scenes in this chapter the player can move to from here, and what has to be true
        first. Connections are one-way; add the reverse on the other scene if needed.
      </p>
      {scene.connections.length === 0 ? (
        <EmptyState title="No connections">
          {scene.siblings.length === 0
            ? "Add another scene to this chapter first."
            : "The player can't leave this scene until you add one."}
        </EmptyState>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>To</th>
              <th>Condition</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {scene.connections.map((c) => (
              <tr key={c.id}>
                <td>
                  {onSelectScene ? (
                    <a
                      href={`/stories/${story.id}/map?scene=${c.toSceneId}`}
                      onClick={(ev) => {
                        ev.preventDefault();
                        onSelectScene(c.toSceneId);
                      }}
                    >
                      {c.toSceneName}
                    </a>
                  ) : (
                    <Link to={`/stories/${story.id}/chapters/${chapter.id}/scenes/${c.toSceneId}`}>
                      {c.toSceneName}
                    </Link>
                  )}{" "}
                  <span className="muted small">
                    (<code>{c.toSceneKey}</code>)
                  </span>
                </td>
                <td>
                  <code>{c.condition || "(always)"}</code>
                </td>
                <td className="row-actions">
                  {onSelectConnection && (
                    <button type="button" className="btn btn-sm" onClick={() => onSelectConnection(c.id)}>
                      Edit
                    </button>
                  )}
                  <InlineConfirm label="Delete" onConfirm={() => remove.mutate(c.id)} busy={remove.isPending} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {targets.length > 0 && (
        <form className="inline-form inline-form-wrap" onSubmit={onAdd}>
          <select value={toSceneId} onChange={(e) => setToSceneId(e.target.value)} aria-label="Target scene">
            <option value="">— scene —</option>
            {targets.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name} ({s.key})
              </option>
            ))}
          </select>
          <ConditionInput value={condition} onChange={setCondition} showHint={false} />
          <button type="submit" className="btn btn-sm" disabled={!valid || create.isPending}>
            Add connection
          </button>
        </form>
      )}
      <MutationError error={create.error ?? remove.error} />
    </section>
  );
}
